import type { ReportPapEventFn } from "@mirage/analytics/AnalyticsProvider";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Cancel_DashRenameLink } from "@mirage/analytics/events/types/cancel_dash_rename_link";
import { PAP_Click_DashRenameLink } from "@mirage/analytics/events/types/click_dash_rename_link";
import { PAP_Rename_DashLink } from "@mirage/analytics/events/types/rename_dash_link";
import { PAP_View_DashStackDetails } from "@mirage/analytics/events/types/view_dash_stack_details";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import { publishEvent, SurveyEvent } from "@mirage/service-feedback";
import { tagged } from "@mirage/service-logging";
import { markDashUserStackRecipientCompleteIfNecessary } from "@mirage/service-onboarding";
import {
  MetricPageName,
  StackDetailsModule,
} from "@mirage/service-operational-metrics/module/constants";
import {
  perfTimeSinceLastNavigateAtom,
  useRecordModuleLatency,
} from "@mirage/service-operational-metrics/module/module";
import {
  getStacksDataCachedTags,
  useStacksDataCachedTags,
} from "@mirage/service-operational-metrics/module/tags";
import { useReportViewedStack } from "@mirage/service-recent-content/hooks/useLastViewedStackInfo";
import { updateStackItem } from "@mirage/service-stacks";
import {
  DEFAULT_SECTION_ID,
  stackDerivePAPProps,
  stackGetShareId,
  stackItemGetName,
  stackItemSetName,
} from "@mirage/service-stacks/service/utils";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import Sentry from "@mirage/shared/sentry";
import { showSnackbar } from "@mirage/shared/snackbar";
import { TwoColumnGrid } from "@mirage/shared/two-column-grid/TwoColumnGrid";
import {
  activeStackAtom,
  activeStackHasWritePermissionsAtom,
  activeStackItemsAtom,
  activeStackItemShortcutsAtom,
  activeStackSearchItemsAtom,
  activeStackSearchItemsNoResultsQueryAtom,
  activeStackSectionsAtom,
} from "@mirage/stacks/ActiveStack/atoms";
import i18n from "@mirage/translations";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { AddStackItemBoxAugRev } from "../AddStackItemMenuContent/AddStackItemBox";
import { EditStackItemModal } from "../EditStackItemModal";
import { isStackOwner } from "../Helpers/Utils";
import { useSortedStacks } from "../hooks";
import Actions, { HeaderActions } from "./Actions";
import { DndProvider } from "./DragAndDrop/Provider";
import styles from "./FullScreenStack.module.css";
import { Header } from "./Header/Header";
import { SharingModule } from "./Modules/SharingModule";
import { StackQuerySuggestions } from "./Modules/StackQuerySuggestions";
import { PasteStackItemAnywhere } from "./PasteStackItemAnywhere";
import { AddItemButtonAugRev } from "./Sections/AddItemButton";
import {
  useFetchContentSuggestions,
  useOpenSuggestedLink,
  useSectionsNavigation,
  useShownContentSuggestions,
} from "./Sections/hooks";
import { Navigation } from "./Sections/Navigation";
import { Sections } from "./Sections/Sections";
import { SectionsHeaderCard } from "./Sections/SectionsHeader";
import { Suggestions } from "./Sections/Suggestions";
import { ShareStackButton } from "./ShareStackButton";
import { StackDetailsTourTooltip } from "./StackDetailsTourTooltip";
import { StackErrorBoundary } from "./StackErrorBoundary";
import { StackNavBar } from "./StackNavBar";
import { StackSideNavigator } from "./StackSideNavigator";
import { useAddLinkToStackWithConfirmation } from "./useAddLinkToStackWithConfirmation";
import { useMutationRequestId, useSessionId } from "./useMutationRequestId";

import type { stacks, users } from "@dropbox/api-v2-client/types";
import { useStackPageAugustRevisionEnabled } from "@mirage/august-revision-hook";
import { useStackChatEnabled } from "@mirage/service-experimentation/useStackChatEnabled";
import classNames from "classnames";
import type { Dispatch, SetStateAction } from "react";
import type { UserInfo } from "./types";
import { useSummarizableStackItems } from "./useSummarizableStackItems";

type FullScreenStackV2Props = {
  currentAccount: users.FullAccount;
  mutationRequestId?: string;
  sessionId?: string;
  setShowSharingModule: Dispatch<SetStateAction<boolean>>;
  setShowSuggestions: Dispatch<SetStateAction<boolean>>;
};

// @deprecated
// Support for FSS stacks is being built in StackDetailPageV3.
export function FullScreenStackV2({
  currentAccount,
  mutationRequestId,
  sessionId,
  setShowSharingModule,
  setShowSuggestions,
}: FullScreenStackV2Props) {
  // Atoms.
  const searchResults = useAtomValue(activeStackSearchItemsAtom); // TODO refactor; these just determine whether to show empty state when search is active
  const stack = useAtomValue(activeStackAtom);
  const items = useAtomValue(activeStackItemsAtom);
  const itemShortcut = useAtomValue(activeStackItemShortcutsAtom);
  const { defaultSection, otherSections } = useAtomValue(
    activeStackSectionsAtom,
  );
  const perfTimeSinceLastNavigate = useAtomValue(perfTimeSinceLastNavigateAtom);
  const activeStackSearchItemsNoResultsQuery = useAtomValue(
    activeStackSearchItemsNoResultsQueryAtom,
  );
  const hasWritePermissions = useAtomValue(activeStackHasWritePermissionsAtom);

  // State and helper variables.
  const [stackItemToEdit, setStackItemToEdit] = useState<
    stacks.StackItemShortcut | undefined
  >(); // TODO maintain elsewhere?
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const triggerEditStackRef = useRef<HTMLInputElement>(null);
  const triggerAddToStackRef = useRef<HTMLDivElement>(null);

  const logger = tagged("FullScreenStackV2.tsx");
  const isFirstPageLoad = perfTimeSinceLastNavigate === 0;
  // isNewStack becomes false right as the stack is created.
  // It's not a reliable way to check if you're on the new stack creation page.
  const isNewStack = !stack?.namespace_id || !items;
  const isOwner = stack ? isStackOwner(stack) : false;
  const defaultUserInfo: UserInfo = {
    displayName: currentAccount.name.display_name,
    profilePhotoUrl: currentAccount.profile_photo_url,
  };

  // Custom hooks.
  const isAugRev = useStackPageAugustRevisionEnabled();
  const isStackChatEnabled = useStackChatEnabled();
  const isOmniboxEnabled = useFeatureFlagValueBool("dash_2025_03_18_omnibox");
  const isMobileSize = useIsMobileSize();
  const { scrollToSection, resetSectionRefs } = useSectionsNavigation();
  useMutationRequestId(mutationRequestId);
  useSessionId(sessionId);
  const { contentSuggestions, isFetchingContentSuggestions } =
    useFetchContentSuggestions();
  const handleShowContentSuggestions = useShownContentSuggestions();
  const handleOpenSuggestedLink = useOpenSuggestedLink();
  const addLinkToStackWithConfirmation =
    useAddLinkToStackWithConfirmation(stack);
  const { reportPapEvent } = useMirageAnalyticsContext();
  useMarkStackReceived(isOwner);
  useReportViewedStack(stack?.namespace_id);
  useMarkStackSeen(reportPapEvent, isNewStack, stack, items);
  useMarkModuleLoadComplete(isNewStack, stack, isOwner, isFirstPageLoad);
  const allStacks = useSortedStacks(); // TODO only used to derive stack item names; refactor to determine this lower down.
  const renameStackItem = useRenameStackItem(isNewStack, stack, reportPapEvent);
  const summarizableItems = useSummarizableStackItems(0);

  // Effects.
  useEffect(() => {
    // TODO remove?
    setTooltipOpen(false);
    // The tooltip relies on Tooltip.Control, which does not automatically move when the UI Refreshes
    // Closing and opening it manually resolves this issue.
    setTimeout(() => {
      setTooltipOpen(true);
    }, 500);
  }, [stack?.namespace_id]);

  useEffect(() => {
    // TODO refactor to establish this effect within useSectionsNavigation.
    resetSectionRefs(stack?.namespace_id ?? "");
  }, [stack?.namespace_id, resetSectionRefs]);

  const showSuggestions = hasWritePermissions;
  useEffect(() => {
    // TODO why is setShowSuggestions a prop?
    setShowSuggestions(showSuggestions ?? false);
  }, [showSuggestions, setShowSuggestions]);

  const showSharingModule =
    hasWritePermissions && stack && stackGetShareId(stack);
  useEffect(() => {
    // TODO why is setShowSharingModule a prop?
    setShowSharingModule(!!showSharingModule);
  }, [showSharingModule, setShowSharingModule]);

  // Handlers.
  const handleError = (e: Error) => {
    logger.error(
      "Error boundary onError thrown inside FullScreenStackV2.tsx",
      e,
    );
    Sentry.captureException(e);
  };
  const handleEditItem = (item: stacks.StackItemShortcut) => {
    if (isNewStack) return;
    reportPapEvent(
      PAP_Click_DashRenameLink({
        ...stackDerivePAPProps(stack),
        featureLine: "stacks",
      }),
    );
    setStackItemToEdit(item);
  };

  const sidebarRowGap = isStackChatEnabled
    ? "Micro Large"
    : isAugRev
      ? "Micro Medium"
      : undefined;

  return (
    <ErrorBoundary fallback={<StackErrorBoundary />} onError={handleError}>
      {isStackChatEnabled && (
        <StackNavBar
          mutationRequestId={mutationRequestId}
          sessionId={sessionId}
          currentAccount={currentAccount}
        />
      )}
      <div
        className={classNames(styles.containerV2, {
          [styles.stackChatRedesign]: isStackChatEnabled,
        })}
      >
        <DndProvider>
          <TwoColumnGrid
            version={
              isAugRev ? (isOmniboxEnabled ? "omnibox" : "august") : "standard"
            }
            mainRowGap="0"
            sidebarRowGap={sidebarRowGap}
            headerChildren={
              <>
                <Header
                  additionalContainerStyles={
                    isAugRev && !isStackChatEnabled
                      ? styles.headerBottomSpacing
                      : undefined
                  }
                  defaultUserInfo={defaultUserInfo}
                  editData={{
                    mutationRequestId,
                    sessionId,
                    canEdit: hasWritePermissions ?? false,
                  }}
                  stack={stack}
                  triggerEditStackRef={triggerEditStackRef}
                  withImmediateAction={
                    isAugRev ? (
                      <HeaderActions
                        stack={stack}
                        currentAccount={currentAccount}
                        menuPlacement="bottom-end"
                        mutationRequestId={mutationRequestId}
                        sessionId={sessionId}
                      />
                    ) : (
                      <Actions
                        showShareLabeledIcon
                        stack={stack}
                        currentAccount={currentAccount}
                        menuPlacement="bottom-end"
                        triggerEditStackRef={
                          hasWritePermissions ? triggerEditStackRef : undefined
                        }
                        mutationRequestId={mutationRequestId}
                        sessionId={sessionId}
                      />
                    )
                  }
                  withRightAction={
                    isAugRev &&
                    /* Order is reversed for Stack Chat */
                    (!isStackChatEnabled ? (
                      <>
                        {!isNewStack && (
                          <ShareStackButton
                            stack={stack}
                            disabled={!currentAccount}
                          />
                        )}
                        {hasWritePermissions && defaultSection && (
                          <AddItemButtonAugRev
                            variant="primary"
                            contentSuggestions={contentSuggestions}
                            loadingSuggestions={isFetchingContentSuggestions}
                            section={defaultSection}
                            // Emphasize add button when there are no stack items yet.
                            emphasize={!items || items.length === 0}
                          />
                        )}
                      </>
                    ) : (
                      hasWritePermissions &&
                      defaultSection && (
                        <AddItemButtonAugRev
                          variant="primary"
                          contentSuggestions={contentSuggestions}
                          loadingSuggestions={isFetchingContentSuggestions}
                          section={defaultSection}
                          // Emphasize add button when there are no stack items yet.
                          emphasize={!items || items.length === 0}
                        >
                          {i18n.t("add")}
                        </AddItemButtonAugRev>
                      )
                    ))
                  }
                />
                {!isAugRev && (
                  <SectionsHeaderCard namespaceId={stack?.namespace_id ?? ""} />
                )}
              </>
            }
            sidebarChildren={
              !isStackChatEnabled ? (
                <>
                  <Navigation scrollToSection={scrollToSection} />
                  {showSharingModule && <SharingModule stack={stack} />}
                  {!isAugRev && showSuggestions && <Suggestions />}
                </>
              ) : (
                <>
                  {!isNewStack && summarizableItems.length > 0 && (
                    <StackQuerySuggestions
                      stackId={stack.sharing_id}
                      summarizableItems={summarizableItems}
                    />
                  )}
                  {showSharingModule && <SharingModule stack={stack} />}
                </>
              )
            }
            mainChildren={
              // Need to manually manage gap for main children due to DND empty sections
              <>
                {isAugRev && !isStackChatEnabled && (
                  <SectionsHeaderCard namespaceId={stack?.namespace_id ?? ""} />
                )}
                {!isNewStack && isStackChatEnabled && !isMobileSize && (
                  <StackSideNavigator scrollToSection={scrollToSection} />
                )}
                {items?.length || searchResults !== null ? (
                  <Sections
                    namespaceId={stack?.namespace_id ?? ""}
                    onEditItem={handleEditItem}
                  />
                ) : (
                  otherSections &&
                  otherSections?.length > 0 && (
                    <Sections
                      namespaceId={stack?.namespace_id ?? ""}
                      onEditItem={handleEditItem}
                    />
                  )
                )}
                {isAugRev && hasWritePermissions && defaultSection && (
                  <div className={styles.addBoxWrapper}>
                    <AddStackItemBoxAugRev
                      addLinkToStackWithConfirmation={
                        addLinkToStackWithConfirmation
                      }
                      containerRef={triggerAddToStackRef}
                      contentSuggestions={contentSuggestions}
                      createStackSessionId={sessionId || ""}
                      existingItems={itemShortcut}
                      focusOnLoad
                      hideRecommendedHeader={!isNewStack}
                      inputPosition="bottom"
                      key={stack?.namespace_id}
                      loadingSuggestions={isFetchingContentSuggestions}
                      onOpenLink={handleOpenSuggestedLink}
                      onShowLinks={handleShowContentSuggestions}
                      providedInputString={
                        activeStackSearchItemsNoResultsQuery || undefined
                      }
                      recommendedEmptyState
                      sectionIdToAddItem={
                        defaultSection.id || DEFAULT_SECTION_ID
                      }
                      stack={stack}
                    />
                    <StackDetailsTourTooltip
                      open={tooltipOpen}
                      stepKey="add-more-files"
                      triggerRef={triggerAddToStackRef}
                    />
                  </div>
                )}
              </>
            }
          />
        </DndProvider>
        {stackItemToEdit && (
          <EditStackItemModal
            originalStackItemName={stackItemGetName(stackItemToEdit, allStacks)}
            isOpen={true}
            onCancel={() => {
              if (!isNewStack) {
                reportPapEvent(
                  PAP_Cancel_DashRenameLink({
                    ...stackDerivePAPProps(stack),
                    featureLine: "stacks",
                  }),
                );
              }
              setStackItemToEdit(undefined);
            }}
            onSubmit={async (data: { name?: string }) => {
              if (!stackItemToEdit || !data.name || !items) return;

              const itemToEdit = items.find(
                (item) =>
                  item[".tag"] === "shortcut" &&
                  item.api_file_id === stackItemToEdit.api_file_id,
              );

              if (!itemToEdit) return;

              renameStackItem(itemToEdit, data.name);
              setStackItemToEdit(undefined);
              showSnackbar({ title: i18n.t("stack_item_updated") });
            }}
          />
        )}
        {hasWritePermissions && <PasteStackItemAnywhere stack={stack} />}
      </div>
    </ErrorBoundary>
  );
}

const useRenameStackItem = (
  isNewStack: boolean,
  stack: stacks.Stack | null,
  reportPapEvent: ReportPapEventFn,
) => {
  const renameStackItem = useCallback(
    async (itemToEdit: stacks.StackItem, newName: string) => {
      if (isNewStack || !stack) {
        return;
      }

      const namespaceId = stack.namespace_id;

      reportPapEvent(
        PAP_Rename_DashLink({
          ...stackDerivePAPProps(stack),
          featureLine: "stacks",
        }),
      );

      await updateStackItem(
        namespaceId ?? "",
        stackItemSetName(itemToEdit, newName),
      );
    },
    [isNewStack, reportPapEvent, stack],
  );

  return renameStackItem;
};

const useMarkStackReceived = (isOwner: boolean) => {
  useEffect(() => {
    if (!isOwner) {
      markDashUserStackRecipientCompleteIfNecessary();
    }
  }, [isOwner]);
};

const useMarkStackSeen = (
  reportPapEvent: ReportPapEventFn,
  isNewStack: boolean,
  stack: stacks.Stack | null,
  items: stacks.StackItem[] | null,
) => {
  const hasSentViewEventForStack = useRef<string | undefined>(undefined);
  const hasSentViewEventForStackItems = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (
      isNewStack ||
      !stack ||
      !items ||
      hasSentViewEventForStack.current === stack.namespace_id ||
      hasSentViewEventForStackItems.current === items.toString()
    ) {
      return;
    }

    if (stack.namespace_id) {
      reportPapEvent(
        PAP_View_DashStackDetails({
          ...stackDerivePAPProps(stack),
          stackSize: items.length,
          featureLine: "stacks",
          stackCreationType: stack.stack_data?.creation_type?.[".tag"],
        }),
      );
      // Publish Sprig event for non-new stacks
      publishEvent(SurveyEvent.OpenStackPage);
    }
    hasSentViewEventForStack.current = stack.namespace_id;
    // TODO avoid .toString()
    hasSentViewEventForStackItems.current = items.toString();
  }, [reportPapEvent, stack, items, isNewStack]);
};

const useMarkModuleLoadComplete = (
  isNewStack: boolean,
  stack: stacks.Stack | null,
  isOwner: boolean,
  isFirstPageLoad: boolean,
) => {
  const tags = useStacksDataCachedTags();
  const { markModuleLoadComplete } = useRecordModuleLatency(
    MetricPageName.STACK_DETAILS,
    StackDetailsModule.VIEW_SHARED_STACK,
    tags || getStacksDataCachedTags,
    /* excludeFromE2E= */ true,
  );

  // Log only for first page loads.
  // The latency for navigations after page load is very small and not worth measuring.
  useEffect(() => {
    if (isNewStack || !stack || isOwner || !isFirstPageLoad) return;
    markModuleLoadComplete();
  }, [isFirstPageLoad, isNewStack, isOwner, markModuleLoadComplete, stack]);
};
